import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * The basket: what the client has picked, and which media-plan row they picked
 * it for.
 *
 * A line is an asset *and* a row, not an asset. The same source image is
 * routinely wanted for four rows across three markets, and each of those is a
 * separate piece of production work — so the pair is the unit that gets
 * ordered, counted and priced. Modelling the basket as a set of asset ids
 * would make the fourth pick silently overwrite the first three.
 *
 * Held in session storage rather than component state alone. Filling a basket
 * takes several minutes of scanning, the tab is the same one people open a
 * shared asset permalink in, and losing the picks to a reload is the kind of
 * thing nobody reports and everybody resents. Session, not local: a basket is
 * worth surviving a refresh, not worth greeting someone with next Tuesday.
 */

export interface BasketLine {
  rowId: string;
  assetId: string;
}

const STORAGE_KEY = 'asset-library.basket';
const DETAILS_KEY = 'asset-library.basket.details';
const OPEN_ON_ARRIVAL_KEY = 'asset-library.basket.open-on-arrival';

/**
 * The saved draft the basket was opened from, if any. While set, saving
 * updates that draft and sending submits it, instead of making new ones.
 */
export interface LinkedDraft {
  id: string;
  /** A draft covers one market; picks from other markets become new briefs. */
  market: string;
  createdAt: string;
  /**
   * The draft's `updated_at` as the basket last saw it. A draft changed since
   * (in My Briefs, say) isn't resumed blindly: the basket's next save would
   * replace its lines and quietly undo that change.
   */
  updatedAt?: string;
}

/** What the basket carries besides its picks: the brief's own fields. */
export interface BasketDetails {
  neededBy: string;
  notes: string;
  draft: LinkedDraft | null;
}

const NO_DETAILS: BasketDetails = { neededBy: '', notes: '', draft: null };

function readDetails(): BasketDetails {
  try {
    const raw = sessionStorage.getItem(DETAILS_KEY);
    if (!raw) return NO_DETAILS;
    const parsed = JSON.parse(raw) as Partial<BasketDetails>;
    const draft = parsed.draft;
    return {
      neededBy: typeof parsed.neededBy === 'string' ? parsed.neededBy : '',
      notes: typeof parsed.notes === 'string' ? parsed.notes : '',
      draft:
        draft &&
        typeof draft.id === 'string' &&
        typeof draft.market === 'string' &&
        typeof draft.createdAt === 'string' &&
        (draft.updatedAt === undefined || typeof draft.updatedAt === 'string')
          ? draft
          : null,
    };
  } catch {
    return NO_DETAILS;
  }
}

function write(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A basket that can't be persisted still works for this page view, which
    // is the case that matters. Nothing to tell the user.
  }
}

/** How many picks the basket holds right now, read from outside the library. */
export function storedBasketCount(): number {
  return read().length;
}

/** The draft the stored basket is linked to, read from outside the library. */
export function storedLinkedDraft(): LinkedDraft | null {
  return readDetails().draft;
}

/**
 * Put a saved draft in the basket, replacing whatever was there, and have the
 * library open the basket when it next loads. Called from My Briefs, which
 * then navigates there: the library reads the basket from session storage as
 * it mounts.
 */
export function openDraftInBasket(
  draft: LinkedDraft,
  lines: BasketLine[],
  details: { neededBy: string; notes: string },
) {
  write(STORAGE_KEY, lines);
  write(DETAILS_KEY, { ...details, draft });
  write(OPEN_ON_ARRIVAL_KEY, true);
}

/** Have the library open the basket when it next loads, picks unchanged. */
export function requestOpenOnArrival() {
  write(OPEN_ON_ARRIVAL_KEY, true);
}

/** Whether to open the basket on arrival — true once, then forgotten. */
export function takeOpenOnArrival(): boolean {
  try {
    const wanted = sessionStorage.getItem(OPEN_ON_ARRIVAL_KEY) === 'true';
    sessionStorage.removeItem(OPEN_ON_ARRIVAL_KEY);
    return wanted;
  } catch {
    return false;
  }
}

function read(): BasketLine[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Filtered rather than trusted: the key is user-writable, and a malformed
    // line would otherwise crash the render it reaches rather than the parse.
    return parsed.filter(
      (line): line is BasketLine =>
        typeof line === 'object' &&
        line !== null &&
        typeof (line as BasketLine).rowId === 'string' &&
        typeof (line as BasketLine).assetId === 'string',
    );
  } catch {
    // Private-mode storage throws on read as well as write.
    return [];
  }
}

export interface Basket {
  lines: BasketLine[];
  /** Line count — the number of localisations, which is what gets ordered. */
  count: number;
  /** Distinct rows in the basket, for "3 rows" summaries. */
  rowCount: number;
  /** Lines grouped by row, in insertion order, for the review list. */
  byRow: Map<string, BasketLine[]>;
  has: (rowId: string, assetId: string) => boolean;
  /** Ids in the basket for this row — what the grid marks as added. */
  assetIdsForRow: (rowId: string) => Set<string>;
  toggle: (rowId: string, assetId: string) => void;
  addMany: (rowId: string, assetIds: string[]) => void;
  remove: (rowId: string, assetId: string) => void;
  removeRow: (rowId: string) => void;
  /** Empties the picks and forgets the details and any linked draft. */
  clear: () => void;
  /** The brief's needed-by date and notes, kept while the basket fills. */
  neededBy: string;
  setNeededBy: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  /** The draft being edited, or null for a basket that will make new briefs. */
  draft: LinkedDraft | null;
  /** Forget the details and the linked draft, keeping any picks left. */
  resetDetails: () => void;
  /** Stop writing to the linked draft; picks, date and notes stay. */
  unlinkDraft: () => void;
  /** Record the linked draft's new `updated_at`, after the basket wrote it. */
  touchDraft: (updatedAt: string) => void;
  /** Replace the whole basket with a saved draft, linked to it. */
  load: (
    draft: LinkedDraft,
    lines: BasketLine[],
    details: { neededBy: string; notes: string },
  ) => void;
}

export function useBasket(): Basket {
  const [lines, setLines] = useState<BasketLine[]>(read);
  // Needed-by and notes live here rather than in the drawer, so they outlive
  // closing it -- which a reopened draft's values have to.
  const [details, setDetails] = useState<BasketDetails>(readDetails);

  useEffect(() => write(STORAGE_KEY, lines), [lines]);
  useEffect(() => write(DETAILS_KEY, details), [details]);

  const setNeededBy = useCallback(
    (neededBy: string) => setDetails((prev) => ({ ...prev, neededBy })),
    [],
  );
  const setNotes = useCallback((notes: string) => setDetails((prev) => ({ ...prev, notes })), []);
  const resetDetails = useCallback(() => setDetails(NO_DETAILS), []);
  const unlinkDraft = useCallback(() => setDetails((prev) => ({ ...prev, draft: null })), []);
  const load = useCallback(
    (draft: LinkedDraft, next: BasketLine[], fields: { neededBy: string; notes: string }) => {
      setLines(next);
      setDetails({ ...fields, draft });
    },
    [],
  );
  const touchDraft = useCallback(
    (updatedAt: string) =>
      setDetails((prev) => (prev.draft ? { ...prev, draft: { ...prev.draft, updatedAt } } : prev)),
    [],
  );

  const has = useCallback(
    (rowId: string, assetId: string) =>
      lines.some((line) => line.rowId === rowId && line.assetId === assetId),
    [lines],
  );

  const remove = useCallback((rowId: string, assetId: string) => {
    setLines((prev) => prev.filter((line) => !(line.rowId === rowId && line.assetId === assetId)));
  }, []);

  const toggle = useCallback((rowId: string, assetId: string) => {
    setLines((prev) => {
      const without = prev.filter((line) => !(line.rowId === rowId && line.assetId === assetId));
      return without.length === prev.length ? [...prev, { rowId, assetId }] : without;
    });
  }, []);

  /**
   * Bulk add is additive and idempotent, so adding a selection that overlaps
   * what is already in the basket tops it up instead of duplicating it. The
   * alternative — treating the selection as the row's basket — would silently
   * drop lines the user added earlier from a different selection.
   */
  const addMany = useCallback((rowId: string, assetIds: string[]) => {
    setLines((prev) => {
      const existing = new Set(
        prev.filter((line) => line.rowId === rowId).map((line) => line.assetId),
      );
      // Deduped before filtering, not just checked against the basket: two
      // copies of the same id in one call would both pass a check made against
      // the pre-call snapshot. No caller does that today, but the guarantee
      // belongs to the hook rather than to its current callers.
      const additions = Array.from(new Set(assetIds))
        .filter((id) => !existing.has(id))
        .map((assetId) => ({ rowId, assetId }));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, []);

  const removeRow = useCallback((rowId: string) => {
    setLines((prev) => prev.filter((line) => line.rowId !== rowId));
  }, []);

  const clear = useCallback(() => {
    setLines([]);
    setDetails(NO_DETAILS);
  }, []);

  const byRow = useMemo(() => {
    const map = new Map<string, BasketLine[]>();
    for (const line of lines) {
      const group = map.get(line.rowId);
      if (group) group.push(line);
      else map.set(line.rowId, [line]);
    }
    return map;
  }, [lines]);

  const assetIdsForRow = useCallback(
    (rowId: string) =>
      new Set(lines.filter((line) => line.rowId === rowId).map((line) => line.assetId)),
    [lines],
  );

  return {
    lines,
    count: lines.length,
    rowCount: byRow.size,
    byRow,
    has,
    assetIdsForRow,
    toggle,
    addMany,
    remove,
    removeRow,
    clear,
    neededBy: details.neededBy,
    setNeededBy,
    notes: details.notes,
    setNotes,
    draft: details.draft,
    resetDetails,
    unlinkDraft,
    touchDraft,
    load,
  };
}
